import axios from 'axios'
import lodash from 'lodash'

/**
 * Saisie minimale avant d'interroger l'autocomplétion d'adresse.
 *
 * Ce seuil vaut aussi côté serveur (`AddressLookupService::MIN_CHARS`) : deux caractères,
 * ni plus ni moins. C'est la règle produit — dès deux caractères, une proposition peut
 * apparaître.
 */
const MIN_CHARS = 2

/**
 * Suggestions d'adresse (Géoplateforme / IGN, Base Adresse Nationale), partagées par les
 * formulaires client.
 *
 * La logique — anti-rebond, appel, repli, message — est identique dans le devis et dans la
 * fiche client ; seuls le balisage et les composants de champ diffèrent. Elle vit donc ici,
 * pour qu'une correction profite aux deux écrans.
 *
 * > [!note] L'adresse reste une simple CHAÎNE.
 * > Choisir une suggestion recopie son texte dans le champ. Aucun identifiant de lieu n'est
 * > conservé, rien n'est décomposé en base.
 *
 * > [!warning] Un fournisseur indisponible ne doit RIEN bloquer.
 * > Le serveur rend une liste vide plutôt qu'une erreur ; `addressFallback` passe alors à
 * > vrai et l'écran propose la saisie manuelle (ville, code postal, pays).
 */
export default {
    data() {
        return {
            addressSuggestions: [],
            addressLoading: false,
            // La frappe en cours : sans elle, impossible de dire si la liste est vide parce
            // qu'on n'a pas encore interrogé, ou parce que rien n'a été proposé.
            addressTerm: '',
            // Vrai dès qu'une recherche s'est achevée sans rien proposer : c'est le signal
            // qui fait apparaître les champs manuels.
            addressFallback: false,
        }
    },
    computed: {
        /**
         * Message sous le champ, ou `null` quand la liste parle d'elle-même.
         *
         * Dérivé et non stocké : `addressTerm`, `addressLoading` et `addressSuggestions`
         * suffisent à décrire l'état, donc rien ne peut devenir faux. Une liste vide doit
         * dire de quoi il retourne — sans message, elle laisse croire à un champ cassé.
         */
        addressMessage() {
            if (this.addressLoading) {
                return this.$t('devisV3.clientSelector.addressSearching')
            }

            if (this.addressSuggestions.length > 0) {
                return null
            }

            const length = String(this.addressTerm || '').trim().length

            if (length >= MIN_CHARS) {
                return this.$t('devisV3.clientSelector.addressNoResult')
            }

            return null
        },
    },
    created() {
        // 400 ms : le service public est gratuit mais lent par pics (11 secondes relevées sur
        // certaines requêtes). Interroger à chaque frappe garderait l'utilisateur devant une
        // liste qui n'arrivera qu'après sa frappe suivante.
        this.debouncedAddressSearch = lodash.debounce(this.runAddressSearch, 400)
    },
    beforeDestroy() {
        this.debouncedAddressSearch?.cancel()
    },
    methods: {
        /**
         * Frappe dans le champ adresse.
         *
         * Deux caractères suffisent à lancer une recherche, comme sur le serveur. En dessous,
         * on ne fait que réinitialiser : rien n'est encore prêt à être interrogé.
         */
        onAddressInput(value) {
            const term = String(value || '').trim()

            this.addressSuggestions = []
            this.addressTerm = term

            if (term.length < MIN_CHARS) {
                this.addressLoading = false
                this.addressFallback = false
                this.debouncedAddressSearch.cancel()

                return
            }

            this.addressLoading = true
            this.debouncedAddressSearch(term)
        },
        async runAddressSearch(value) {
            const term = String(value || '').trim()

            if (term.length < MIN_CHARS) return

            try {
                const { data } = await axios.get('/back-office/v3/address/autocomplete', { params: { q: term } })
                this.addressSuggestions = data.data || []
            } catch (error) {
                // Une panne ne doit rien bloquer : on bascule sur la saisie manuelle.
                console.error('Address autocomplete failed:', error)
                this.addressSuggestions = []
            } finally {
                this.addressLoading = false
                // Aucune proposition — fournisseur muet, en panne, ou adresse inconnue de lui :
                // la saisie est conservée telle quelle, une proposition pourra survenir ensuite.
                this.addressFallback = this.addressSuggestions.length === 0
            }
        },
        /**
         * Referme la liste sans rien choisir.
         *
         * La frappe courante est oubliée avec : le champ vient d'être rempli, et le message
         * « aucune adresse trouvée » ne doit surtout pas s'afficher dessous.
         */
        clearAddressSuggestions() {
            this.addressSuggestions = []
            this.addressLoading = false
            this.addressTerm = ''
        },
    },
}
